import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Download, FileText, Loader2, PanelRightClose, ZoomIn, ZoomOut } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { configurePdfJsWorker } from "../../../lib/pdfWorker";
import {
  base64PdfToBlob,
  downloadBase64Pdf,
  titleCasePdfName,
} from "../../../lib/chatPdfApi";

configurePdfJsWorker(pdfjs);

const PDF_DOCUMENT_OPTIONS = { isEvalSupported: false };

function ArtifactPdfSkeleton() {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
      <Loader2 className="h-8 w-8 animate-spin opacity-60" aria-hidden />
      <p className="text-sm">Rendering preview...</p>
    </div>
  );
}

export default function ChatPdfArtifactPane({ attachment, className, overlayMode = false, onClose }) {
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const scrollRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(480);
  const [zoomStep, setZoomStep] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const filename = titleCasePdfName(attachment?.filename);
  const zoomFactor = useMemo(() => 1 + zoomStep * 0.15, [zoomStep]);
  const pageWidth = Math.max(160, Math.floor(viewportWidth * zoomFactor));

  useEffect(() => {
    if (!attachment?.data) {
      setPdfBlobUrl(null);
      return undefined;
    }

    let objectUrl = null;
    try {
      objectUrl = URL.createObjectURL(base64PdfToBlob(attachment.data));
      setPdfBlobUrl(objectUrl);
    } catch (err) {
      console.error("[ChatPdfArtifactPane] PDF blob error:", err);
      setPdfBlobUrl(null);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.data]);

  useEffect(() => {
    setLoadError(null);
    setNumPages(null);
    setZoomStep(0);
  }, [pdfBlobUrl]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? root.clientWidth;
      setViewportWidth(Math.max(120, Math.floor(w - 32)));
    });

    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  const handleDownload = useCallback(async () => {
    if (!attachment?.data || downloading) return;
    setDownloading(true);
    try {
      downloadBase64Pdf(attachment.data, filename);
    } finally {
      setDownloading(false);
    }
  }, [attachment?.data, filename, downloading]);

  const handleDocLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setLoadError(null);
  }, []);

  const handleDocLoadError = useCallback((err) => {
    console.error("[ChatPdfArtifactPane] PDF load error:", err);
    setLoadError(true);
    setNumPages(null);
  }, []);

  if (!attachment?.data || !pdfBlobUrl) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 bg-muted/20 px-4 text-center text-sm text-muted-foreground",
          className
        )}
      >
        <FileText className="h-10 w-10 opacity-40" aria-hidden />
        <p>PDF preview unavailable.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border-l border-border/80 bg-[hsl(220,14%,96%)] shadow-[inset_1px_0_0_hsl(220,13%,91%)] dark:border-border dark:bg-[hsl(240,6%,10%)] dark:shadow-[inset_1px_0_0_hsl(240,4%,18%)]",
        className
      )}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/70 bg-background/85 px-3 py-2.5 backdrop-blur-md dark:bg-[hsl(240,5%,12%)]/90">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="hidden shrink-0 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline">
            PDF
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-500/12 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400">
              <FileText className="h-4 w-4" strokeWidth={2} aria-hidden />
            </div>
            <span className="truncate text-sm font-medium text-foreground" title={filename}>
              {filename}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            disabled={zoomStep <= -3}
            onClick={() => setZoomStep((z) => z - 1)}
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="hidden min-w-[2.75rem] text-center text-[11px] font-medium tabular-nums text-muted-foreground sm:inline">
            {Math.round(zoomFactor * 100)}%
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            disabled={zoomStep >= 5}
            onClick={() => setZoomStep((z) => z + 1)}
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("relative h-8 w-8", downloading && "mr-2")}
            disabled={downloading}
            aria-busy={downloading}
            onClick={handleDownload}
            aria-label={downloading ? "Downloading PDF" : "Download PDF"}
          >
            <span className={cn("inline-flex", downloading && "invisible")}>
              <Download className="h-4 w-4" aria-hidden />
            </span>
            {downloading ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <LoadingSpinner />
              </span>
            ) : null}
          </Button>
          {overlayMode && typeof onClose === "function" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-orange-500 hover:bg-orange-500/10 hover:text-orange-600"
              onClick={onClose}
              aria-label="Close preview"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </header>

      <div
        ref={scrollRef}
        className="chat-scrollbar relative min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[hsl(220,14%,94%)]/90 dark:bg-black/25"
      >
        <div className="mx-auto flex w-full max-w-full flex-col items-center gap-5 px-4 py-6 pb-10">
          {loadError ? (
            <div className="rounded-xl border border-border bg-background px-6 py-8 text-center text-sm text-muted-foreground shadow-sm">
              <p className="font-medium text-foreground">Could not render this PDF inline.</p>
              <p className="mt-2 text-muted-foreground">Use download to open it in your PDF app.</p>
              <Button
                type="button"
                className="relative mt-4"
                size="sm"
                disabled={downloading}
                aria-busy={downloading}
                onClick={handleDownload}
              >
                <span className={cn("inline-flex items-center", downloading && "invisible")}>
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  Download
                </span>
                {downloading ? (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner />
                  </span>
                ) : null}
              </Button>
            </div>
          ) : (
            <Document
              key={pdfBlobUrl}
              file={pdfBlobUrl}
              loading={<ArtifactPdfSkeleton />}
              onLoadSuccess={handleDocLoadSuccess}
              onLoadError={handleDocLoadError}
              options={PDF_DOCUMENT_OPTIONS}
              className="flex w-full flex-col items-center gap-5"
            >
              {numPages
                ? Array.from({ length: numPages }, (_, i) => (
                    <div
                      key={`page_${i + 1}`}
                      className="rounded-md bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08),0_4px_24px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.06] dark:bg-[hsl(240,5%,14%)] dark:shadow-none dark:ring-white/[0.06]"
                    >
                      <Page
                        pageNumber={i + 1}
                        width={pageWidth}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="block [&_.react-pdf__Page__canvas]:block"
                      />
                    </div>
                  ))
                : null}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}
